import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errorMessage } from "../../lib/api";
import { GROUP_LABELS, groupFields } from "../../lib/fieldMeta";
import { useLang } from "../../lib/i18n";
import { CustomFieldDialog } from "./CustomFieldDialog";

const iconBtn = "flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 hover:text-slate-900";

const FieldRow = ({ f, onChange, onEdit, onDelete }) => {
  const { t, pick } = useLang();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: f.key });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <tr ref={setNodeRef} style={style} data-testid={`field-row-${f.key}`}
      className={`border-t border-slate-100 ${isDragging ? "relative z-10 bg-white shadow-lg" : ""}`}>
      <td className="px-2 py-3">
        <button type="button" {...attributes} {...listeners} title={t("Drag to reorder")} data-testid={`field-drag-${f.key}`}
          className="flex h-9 w-9 cursor-grab touch-none items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 active:cursor-grabbing">
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="px-3 py-3">
        <p className="text-sm font-semibold text-slate-900">
          {pick(f.label_en, f.label_ar)}
          {f.custom && <span className="ms-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{t("Custom")}</span>}
        </p>
        <p className="text-start text-xs text-slate-500">{pick(f.label_ar, f.label_en)}</p>
      </td>
      <td className="px-5 py-3">
        <Switch checked={f.enabled} onCheckedChange={(v) => onChange(f.key, { enabled: v, required: v && f.required })} data-testid={`field-enabled-${f.key}`} />
      </td>
      <td className="px-5 py-3">
        <Switch checked={f.required} disabled={!f.enabled} onCheckedChange={(v) => onChange(f.key, { required: v })} data-testid={`field-required-${f.key}`} />
      </td>
      <td className="px-5 py-3">
        <Select value={f.editable_by} onValueChange={(v) => onChange(f.key, { editable_by: v })} disabled={!f.enabled}>
          <SelectTrigger className="h-10 w-44 rounded-xl" data-testid={`field-editable-${f.key}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="employee" data-testid={`field-editable-${f.key}-employee`}>{t("Employee can edit")}</SelectItem>
            <SelectItem value="admin" data-testid={`field-editable-${f.key}-admin`}>{t("Admin only")}</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td className="px-5 py-3">
        {f.custom && (
          <div className="flex justify-end gap-1">
            <button type="button" onClick={() => onEdit(f)} className={iconBtn} data-testid={`field-edit-${f.key}`} title={t("Edit field")}><Pencil className="h-4 w-4" /></button>
            <button type="button" onClick={() => onDelete(f)} className={`${iconBtn} hover:bg-red-50 hover:text-wade`} data-testid={`field-delete-${f.key}`} title={t("Delete field")}><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </td>
    </tr>
  );
};

const GroupTable = ({ group, list, sensors, onDragEnd, rowProps }) => {
  const { t, lang } = useLang();
  return (
    <div className="overflow-x-auto rounded-3xl border border-slate-200/80 bg-white" data-testid={`fields-group-${group}`}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <table className="w-full min-w-[760px] table-fixed text-start">
          <colgroup>
            <col className="w-14" /><col /><col className="w-28" /><col className="w-28" /><col className="w-56" /><col className="w-28" />
          </colgroup>
          <thead>
            <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              <th />
              <th className="px-3 py-3 text-start font-display text-sm normal-case tracking-normal text-slate-900">{GROUP_LABELS[group][lang]}</th>
              <th className="px-5 py-3 text-start">{t("Enabled")}</th><th className="px-5 py-3 text-start">{t("Mandatory")}</th>
              <th className="px-5 py-3 text-start">{t("Who can edit")}</th><th className="px-5 py-3" />
            </tr>
          </thead>
          <SortableContext items={list.map((f) => f.key)} strategy={verticalListSortingStrategy}>
            <tbody>{list.map((f) => <FieldRow key={f.key} f={f} {...rowProps} />)}</tbody>
          </SortableContext>
        </table>
      </DndContext>
    </div>
  );
};

export const FieldsPanel = () => {
  const { t, pick } = useLang();
  const [fields, setFields] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState({ open: false, field: null });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  useEffect(() => { api.get("/fields").then(({ data }) => setFields(data.fields)); }, []);
  const update = (key, patch) => setFields((list) => list.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const save = async () => {
    setSaving(true);
    const body = Object.fromEntries(fields.map((f) => [f.key, { enabled: f.enabled, required: f.required, editable_by: f.editable_by }]));
    try {
      const { data } = await api.put("/admin/settings/fields", { fields: body });
      setFields(data.fields);
      toast.success(t("Form fields saved"));
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const keys = fields.map((f) => f.key);
    const next = arrayMove(fields, keys.indexOf(active.id), keys.indexOf(over.id));
    setFields(next);
    try {
      await api.put("/admin/settings/fields/order", { keys: next.map((f) => f.key) });
      toast.success(t("Order saved"));
    } catch (e) { toast.error(errorMessage(e)); setFields(fields); }
  };

  const remove = async (f) => {
    if (!window.confirm(t("Delete \"{name}\"? Its values will be removed from every card and template.", { name: pick(f.label_en, f.label_ar) }))) return;
    try {
      const { data } = await api.delete(`/admin/settings/fields/custom/${f.key}`);
      setFields(data.fields);
      toast.success(t("Field deleted"));
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!fields) return <div className="h-64 animate-pulse rounded-3xl bg-slate-200" />;
  const rowProps = { onChange: update, onEdit: (field) => setDialog({ open: true, field }), onDelete: remove };
  return (
    <div className="space-y-6" data-testid="fields-panel">
      <div className="flex flex-wrap items-end gap-4">
        <p className="me-auto max-w-xl text-sm text-slate-500">
          {t("Name, title and email are always on and managed by admins. Disabled fields are hidden from cards and forms. Drag rows to change the order on the card.")}
        </p>
        <button onClick={() => setDialog({ open: true, field: null })} data-testid="fields-add-button"
          className="btn-press flex min-h-[50px] items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 font-semibold text-slate-900 hover:border-slate-900">
          <Plus className="h-4 w-4" /> {t("Add field")}
        </button>
        <button onClick={save} disabled={saving} data-testid="fields-save-button"
          className="btn-press min-h-[50px] rounded-2xl bg-slate-900 px-6 font-semibold text-white disabled:opacity-60">
          {saving ? t("Saving…") : t("Save fields")}
        </button>
      </div>
      {groupFields(fields).map(([group, list]) => (
        <GroupTable key={group} group={group} list={list} sensors={sensors} onDragEnd={onDragEnd} rowProps={rowProps} />
      ))}
      <CustomFieldDialog open={dialog.open} field={dialog.field} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={setFields} />
    </div>
  );
};
